import { FC } from 'react';
import { Button, Col, Row, Typography } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowRight, TbX } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { SettingsCalloutCard } from '@/shared';
import { ButtonSkeleton } from '@/shared/components/skeletons';
import { EMAIL_LINKS } from '@/shared/constants/externalLinks';
import { formatStripeMoney } from '@/utilities';

interface InvoiceBreakdownProps {
    seats: number;
    seatPrice: number;
    seatPlan: string;
    interval: string;
    addons?: {
        key: string;
        label: string;
        priceId: string;
        quantity: number;
        billingInterval: string;
        unitAmount: number;
        enabled: boolean;
    }[];
    isFree?: boolean;
    isEnterprise?: boolean;
}

const InvoiceBreakdown: FC<InvoiceBreakdownProps> = ({
    isFree,
    isEnterprise,
    seats,
    seatPlan,
    seatPrice,
    interval,
    addons = [],
}): JSX.Element => {
    const lineTotal = (qty: number, price: number) => qty * price;

    const navigate = useNavigate();

    if (isFree || isEnterprise) {
        return (
            <>
                <Typography.Title level={5}>Invoice Breakdown</Typography.Title>
                <SettingsCalloutCard
                    description={
                        isEnterprise
                            ? 'Your workspace is currently on an Enterprise plan. Billing is managed through your enterprise agreement with Countercyclical.'
                            : "You're currently on the Free plan. Upgrade to unlock seats, advanced features, and usage-based capabilities for your workspace."
                    }
                    style={{ marginTop: '1rem' }}
                    title={isEnterprise ? 'Enterprise Plan' : 'Free Plan'}
                >
                    {isEnterprise ? (
                        <Button type='primary' href={EMAIL_LINKS.enterpriseBilling}>
                            Contact Enterprise Team
                            <TbArrowRight />
                        </Button>
                    ) : (
                        <Button type='primary' onClick={() => navigate('/settings/account/plans/seats')}>
                            Upgrade Seats
                            <TbArrowRight />
                        </Button>
                    )}
                </SettingsCalloutCard>
            </>
        );
    }

    return (
        <>
            <Typography.Title level={5}>Invoice Breakdown</Typography.Title>
            {/* Seats */}
            <Row justify='space-between' align='middle' style={{ margin: '0.5rem 0' }}>
                <Col>
                    <div style={{ marginBottom: '0.25rem' }}>
                        <Typography.Text strong>Seats</Typography.Text>
                    </div>
                    {seats && seatPrice ? (
                        <Typography.Text type='secondary'>
                            {seats} Active Seats <TbX style={{ verticalAlign: '-0.125rem' }} /> {seatPlan} (
                            {formatStripeMoney(seatPrice)} / {interval})
                        </Typography.Text>
                    ) : (
                        <ButtonSkeleton active size='small' style={{ width: 16 * 12 }} />
                    )}
                </Col>
                <Col>
                    {seats && seatPrice ? (
                        <Typography.Text strong>
                            {formatStripeMoney(lineTotal(seats, Number(seatPrice)))}
                        </Typography.Text>
                    ) : (
                        <ButtonSkeleton active size='small' style={{ width: 16 * 8 }} />
                    )}
                </Col>
            </Row>

            {/* Feature Add-Ons */}
            {addons.map((addon) => (
                <Row key={addon.key} align='middle' justify='space-between'>
                    <Col>
                        <div style={{ marginBottom: '0.25rem' }}>
                            <Typography.Text strong>Add-Ons</Typography.Text>
                        </div>
                        {addon.key ? (
                            <Typography.Text type='secondary'>
                                {addon?.quantity} Active Seats <TbX style={{ verticalAlign: '-0.125rem' }} />{' '}
                                {addon?.label ?? capitalCase(addon?.key)} ({formatStripeMoney(addon?.unitAmount)} /{' '}
                                {interval})
                            </Typography.Text>
                        ) : (
                            <ButtonSkeleton active size='small' style={{ width: 16 * 12 }} />
                        )}
                    </Col>
                    <Col>
                        {addon.unitAmount ? (
                            <Typography.Text strong>
                                {formatStripeMoney(lineTotal(addon?.quantity, Number(addon?.unitAmount)))}
                            </Typography.Text>
                        ) : (
                            <ButtonSkeleton active size='small' style={{ width: 16 * 8 }} />
                        )}
                    </Col>
                </Row>
            ))}

            {/* Prepaid Credits */}
            {/* {credits.map((credit) => (
                <Row key={credit.name} justify='space-between'>
                    <Col>
                        <Typography.Text>{credit.name}</Typography.Text>
                    </Col>
                    <Col>
                        <Typography.Text>{formatCurrency.format(credit.amount)}</Typography.Text>
                    </Col>
                </Row>
            ))} */}
        </>
    );
};

export default InvoiceBreakdown;
